import { useState, useEffect, useCallback } from "react";
import { formatDateTime } from "@/lib/dateUtils";
import { format } from "date-fns";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { Trash2, Pencil, Save, Home, ChevronRight, ToggleLeft, ToggleRight } from "lucide-react";

// ─── Zod schema ────────────────────────────────────────────────────────────────
const schema = z.object({
  date: z.string().min(1, "Date is required"),
  party_type: z.string().min(1, "Party Type is required"),
  party_name: z.string().min(1, "Party Name is required"),
  phone_number: z
    .string()
    .refine((v) => !v || v.length === 10, { message: "Must be 10 digits" })
    .optional(),
  email: z
    .string()
    .refine((v) => !v || z.string().email().safeParse(v).success, { message: "Invalid email" })
    .optional(),
  address: z.string().optional(),
  opening_balance: z.coerce.number().min(0),
  opening_date: z.string().optional(),
  description: z.string().optional(),
});

type FormData = z.infer<typeof schema>;

// ─── Defaults ──────────────────────────────────────────────────────────────────
const blankForm = (): FormData => ({
  date: new Date().toISOString().split("T")[0],
  party_type: "",
  party_name: "",
  phone_number: "",
  email: "",
  address: "",
  opening_balance: 0,
  opening_date: new Date().toISOString().split("T")[0],
  description: "",
});

// ─── Component ─────────────────────────────────────────────────────────────────
const BusinessParty = () => {
  const [parties, setParties] = useState<any[]>([]);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [itemsPerPage, setItemsPerPage] = useState<number | "all">("all");
  const [currentPage, setCurrentPage] = useState(1);
  const { toast } = useToast();

  const {
    register,
    handleSubmit,
    control,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: blankForm(),
  });

  // ── Fetch ──────────────────────────────────────────────────────────────────
  const fetchParties = useCallback(async () => {
    try {
      setIsLoading(true);
      const res = await fetch("/api/business-parties", { credentials: "include" });
      const json = await res.json();
      if (json.ok) setParties(json.rows || []);
      else toast({ title: "Error", description: json.error || "Failed to load", variant: "destructive" });
    } catch {
      toast({ title: "Error", description: "Network error loading parties", variant: "destructive" });
    } finally {
      setIsLoading(false);
    }
  }, [toast]);

  useEffect(() => { fetchParties(); }, [fetchParties]);

  // ── Submit ─────────────────────────────────────────────────────────────────
  const onSubmit = async (data: FormData) => {
    const url = editingId ? `/api/business-parties/${editingId}` : "/api/business-parties";
    const method = editingId ? "PUT" : "POST";
    try {
      const res = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(data),
      });
      const json = await res.json();
      if (json.ok) {
        toast({ title: "Success", description: editingId ? "Party updated" : "Party saved" });
        setEditingId(null);
        reset(blankForm());
        fetchParties();
      } else {
        toast({ title: "Error", description: json.error || "Failed to save", variant: "destructive" });
      }
    } catch {
      toast({ title: "Error", description: "Network error saving party", variant: "destructive" });
    }
  };

  // ── Edit ───────────────────────────────────────────────────────────────────
  const handleEdit = (item: any) => {
    setEditingId(item.id);
    reset({
      date: item.date ? new Date(item.date).toISOString().split("T")[0] : blankForm().date,
      party_type: item.party_type || "",
      party_name: item.party_name || "",
      phone_number: item.phone_number || "",
      email: item.email || "",
      address: item.address || "",
      opening_balance: parseFloat(item.opening_balance) || 0,
      opening_date: item.opening_date ? new Date(item.opening_date).toISOString().split("T")[0] : "",
      description: item.description || "",
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // ── Delete ─────────────────────────────────────────────────────────────────
  const handleDelete = async (id: string) => {
    try {
      const res = await fetch(`/api/business-parties/${id}`, { method: "DELETE", credentials: "include" });
      const json = await res.json();
      if (json.ok) {
        toast({ title: "Deleted", description: "Party removed" });
        fetchParties();
      } else {
        toast({ title: "Error", description: json.error || "Failed to delete", variant: "destructive" });
      }
    } catch {
      toast({ title: "Error", description: "Network error deleting party", variant: "destructive" });
    }
    setDeleteConfirm(null);
  };

  // ── Toggle Status ──────────────────────────────────────────────────────────
  const toggleStatus = async (id: string, current: boolean) => {
    try {
      const res = await fetch(`/api/business-parties/${id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ is_active: !current }),
      });
      const json = await res.json();
      if (json.ok) {
        toast({ title: "Updated", description: !current ? "Party activated" : "Party deactivated" });
        fetchParties();
      } else {
        toast({ title: "Error", description: json.error || "Failed to update", variant: "destructive" });
      }
    } catch {
      toast({ title: "Error", description: "Network error updating status", variant: "destructive" });
    }
  };

  // ── Pagination ─────────────────────────────────────────────────────────────
  const filtered = parties.filter(
    (p) =>
      (p.party_name || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
      (p.party_type || "").toLowerCase().includes(searchTerm.toLowerCase())
  );
  const total = filtered.length;
  const pages = itemsPerPage === "all" ? 1 : Math.max(1, Math.ceil(total / (itemsPerPage as number)));
  const start = itemsPerPage === "all" ? 0 : (currentPage - 1) * (itemsPerPage as number);
  const end = itemsPerPage === "all" ? total : start + (itemsPerPage as number);
  const rows = itemsPerPage === "all" ? filtered : filtered.slice(start, end);

  // ── Helpers ────────────────────────────────────────────────────────────────
  const renderBalance = (item: any) => {
    const bal = parseFloat(item.opening_balance || 0);
    if (!bal) return <span className="text-slate-400 text-xs">N/A</span>;
    return (
      <div className="text-xs leading-5">
        <div>Balance : {bal.toFixed(2)}</div>
        {item.opening_date && <div>Date : {format(new Date(item.opening_date), "yyyy-MM-dd")}</div>}
      </div>
    );
  };

  const renderLog = (item: any) => (
    <div className="text-xs text-slate-500 leading-5">
      {item.created_at && (
        <div>Created: {item.created_by || "Super Admin"} {formatDateTime(item.created_at)}</div>
      )}
      {item.updated_by && item.updated_at && item.updated_at !== item.created_at && (
        <div>Modified: {item.updated_by} {formatDateTime(item.updated_at)}</div>
      )}
    </div>
  );

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className="space-y-4 bg-slate-50 min-h-full">
      {/* Breadcrumb */}
      <div className="h-10 -mx-6 -mt-6 px-6 flex items-center bg-white border-b border-slate-200">
        <div className="flex items-center gap-1.5 text-xs text-slate-600">
          <Home className="h-3.5 w-3.5 text-slate-800" />
          <span className="font-semibold text-slate-800">Dashboard</span>
          <ChevronRight className="h-3 w-3 text-slate-400" />
          <span>Busi. Cr/Dr Party</span>
        </div>
      </div>

      {/* ── Form Card ── */}
      <Card className="border-0 shadow-md overflow-hidden">
        <CardHeader className="bg-blue-600 py-3 px-6">
          <CardTitle className="text-sm font-semibold text-white">Create Busi. Cr/Dr Party</CardTitle>
        </CardHeader>
        <CardContent className="bg-blue-600 px-6 pb-6 pt-4">
          <form onSubmit={handleSubmit(onSubmit)} noValidate>
            {/* Row 1 */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
              {/* Date */}
              <div>
                <Input
                  type="date"
                  {...register("date")}
                  className="bg-white border-0 h-9 text-sm"
                  placeholder="Date*"
                />
                {errors.date && <p className="text-red-300 text-xs mt-0.5">{errors.date.message}</p>}
              </div>

              {/* Party Type — using Controller so value registers in RHF */}
              <div>
                <Controller
                  name="party_type"
                  control={control}
                  render={({ field }) => (
                    <Select value={field.value} onValueChange={field.onChange}>
                      <SelectTrigger className="bg-white border-0 h-9 text-sm">
                        <SelectValue placeholder="Party Type*" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Bank">Bank</SelectItem>
                        <SelectItem value="Capital">Capital</SelectItem>
                        <SelectItem value="Cash">Cash</SelectItem>
                        <SelectItem value="Creditor">Creditor</SelectItem>
                        <SelectItem value="Owner">Owner</SelectItem>
                        <SelectItem value="Tanker">Tanker</SelectItem>
                      </SelectContent>
                    </Select>
                  )}
                />
                {errors.party_type && <p className="text-red-300 text-xs mt-0.5">{errors.party_type.message}</p>}
              </div>

              {/* Party Name */}
              <div>
                <Input
                  {...register("party_name")}
                  className="bg-white border-0 h-9 text-sm"
                  placeholder="Party Name*"
                />
                {errors.party_name && <p className="text-red-300 text-xs mt-0.5">{errors.party_name.message}</p>}
              </div>

              {/* Phone */}
              <div>
                <Input
                  {...register("phone_number")}
                  className="bg-white border-0 h-9 text-sm"
                  placeholder="Phone Num"
                  maxLength={10}
                />
                {errors.phone_number && <p className="text-red-300 text-xs mt-0.5">{errors.phone_number.message}</p>}
              </div>
            </div>

            {/* Row 2 */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
              {/* Email */}
              <div>
                <Input
                  type="email"
                  {...register("email")}
                  className="bg-white border-0 h-9 text-sm"
                  placeholder="Email"
                />
                {errors.email && <p className="text-red-300 text-xs mt-0.5">{errors.email.message}</p>}
              </div>

              {/* Address */}
              <div>
                <Input
                  {...register("address")}
                  className="bg-white border-0 h-9 text-sm"
                  placeholder="Address"
                />
              </div>

              {/* Opening Balance */}
              <div>
                <Input
                  type="number"
                  step="0.01"
                  {...register("opening_balance", { valueAsNumber: true })}
                  className="bg-white border-0 h-9 text-sm"
                  placeholder="Opening Balance"
                />
              </div>

              {/* Opening Date */}
              <div>
                <Input
                  type="date"
                  {...register("opening_date")}
                  className="bg-white border-0 h-9 text-sm"
                />
                <p className="text-blue-200 text-xs mt-0.5">
                  Balance Cutoff Date (Ex:31-Mar-202X)
                </p>
              </div>
            </div>

            {/* Row 3 */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
              {/* Description */}
              <div>
                <Input
                  {...register("description")}
                  className="bg-white border-0 h-9 text-sm"
                  placeholder="Description"
                />
              </div>
            </div>

            {/* Buttons */}
            <div className="flex justify-center gap-3">
              <Button
                type="submit"
                disabled={isSubmitting}
                className="bg-[#84cc16] hover:bg-[#65a30d] text-white px-10 h-9 font-bold text-sm rounded-full gap-2"
              >
                <Save className="h-4 w-4" />
                {isSubmitting ? "Saving…" : editingId ? "UPDATE" : "SAVE"}
              </Button>
              {editingId && (
                <Button
                  type="button"
                  variant="outline"
                  className="h-9 px-6 text-white border-white hover:bg-blue-700 text-sm"
                  onClick={() => { setEditingId(null); reset(blankForm()); }}
                >
                  Cancel
                </Button>
              )}
            </div>
          </form>
        </CardContent>
      </Card>

      {/* ── Table Card ── */}
      <div className="rounded-lg border border-slate-200 bg-white shadow-md overflow-hidden">
        {/* Toolbar */}
        <div className="px-4 py-3 flex items-center justify-between border-b border-slate-100 bg-slate-50">
          <div className="flex items-center gap-2 text-xs text-slate-700">
            <span className="font-medium">Show:</span>
            <select
              className="h-7 border border-slate-300 bg-white px-2 text-xs outline-none rounded"
              value={itemsPerPage}
              onChange={(e) => {
                setItemsPerPage(e.target.value === "all" ? "all" : Number(e.target.value));
                setCurrentPage(1);
              }}
            >
              <option value="all">All</option>
              <option value="10">10</option>
              <option value="25">25</option>
              <option value="50">50</option>
              <option value="100">100</option>
            </select>
          </div>
          <div className="flex items-center gap-2 text-xs text-slate-700">
            <span>Filter:</span>
            <Input
              placeholder="Type to filter..."
              className="h-7 w-48 text-xs border-slate-300"
              value={searchTerm}
              onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
            />
          </div>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full text-xs border-collapse">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50 text-slate-700">
                <th className="px-3 py-2.5 text-left font-bold whitespace-nowrap">Sl.No</th>
                <th className="px-3 py-2.5 text-center font-bold whitespace-nowrap">Action</th>
                <th className="px-3 py-2.5 text-left font-bold whitespace-nowrap">Status</th>
                <th className="px-3 py-2.5 text-left font-bold whitespace-nowrap">Date</th>
                <th className="px-3 py-2.5 text-left font-bold whitespace-nowrap">Party Type</th>
                <th className="px-3 py-2.5 text-left font-bold whitespace-nowrap">Opening Balance</th>
                <th className="px-3 py-2.5 text-left font-bold whitespace-nowrap">Name</th>
                <th className="px-3 py-2.5 text-left font-bold whitespace-nowrap">Phone Num</th>
                <th className="px-3 py-2.5 text-left font-bold whitespace-nowrap">Email</th>
                <th className="px-3 py-2.5 text-left font-bold whitespace-nowrap">Description</th>
                <th className="px-3 py-2.5 text-left font-bold whitespace-nowrap">User Log Details</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan={11} className="px-3 py-10 text-center text-slate-400">Loading…</td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={11} className="px-3 py-10 text-center text-slate-400">
                    No business parties found. Add one above.
                  </td>
                </tr>
              ) : (
                rows.map((item, idx) => {
                  const active = item.is_active ?? true;
                  return (
                    <tr
                      key={item.id}
                      className="border-b border-slate-100 odd:bg-white even:bg-slate-50 hover:bg-blue-50 transition-colors"
                    >
                      {/* Sl.No */}
                      <td className="px-3 py-2.5 text-slate-600">{start + idx + 1}</td>

                      {/* Action */}
                      <td className="px-3 py-2.5 text-center">
                        <div className="flex items-center justify-center gap-2">
                          <button
                            onClick={() => handleEdit(item)}
                            className="text-blue-500 hover:text-blue-700 transition"
                            title="Edit"
                          >
                            <Pencil className="h-3.5 w-3.5" />
                          </button>
                          <button
                            onClick={() => setDeleteConfirm(item.id)}
                            className="text-red-500 hover:text-red-700 transition"
                            title="Delete"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      </td>

                      {/* Status toggle */}
                      <td className="px-3 py-2.5">
                        <button
                          onClick={() => toggleStatus(item.id, active)}
                          title={active ? "Click to deactivate" : "Click to activate"}
                          className="flex items-center gap-1.5 group"
                        >
                          {active ? (
                            <ToggleRight className="h-6 w-6 text-green-500 group-hover:text-green-600 transition" />
                          ) : (
                            <ToggleLeft className="h-6 w-6 text-slate-400 group-hover:text-slate-500 transition" />
                          )}
                          <span
                            className={`text-xs font-semibold ${
                              active ? "text-green-600" : "text-slate-500"
                            }`}
                          >
                            {active ? "Activated" : "Inactive"}
                          </span>
                        </button>
                      </td>

                      {/* Date */}
                      <td className="px-3 py-2.5 whitespace-nowrap text-slate-700">
                        {item.date ? format(new Date(item.date), "dd-MMM-yyyy") : "—"}
                      </td>

                      {/* Party Type */}
                      <td className="px-3 py-2.5 text-slate-700">{item.party_type || "—"}</td>

                      {/* Opening Balance */}
                      <td className="px-3 py-2.5">{renderBalance(item)}</td>

                      {/* Name */}
                      <td className="px-3 py-2.5 font-semibold text-slate-800">{item.party_name || "—"}</td>

                      {/* Phone */}
                      <td className="px-3 py-2.5 text-slate-700">{item.phone_number || "0"}</td>

                      {/* Email */}
                      <td className="px-3 py-2.5 text-slate-700 max-w-[120px] truncate">{item.email || ""}</td>

                      {/* Description */}
                      <td className="px-3 py-2.5 text-slate-700 max-w-[100px] truncate">{item.description || ""}</td>

                      {/* Log */}
                      <td className="px-3 py-2.5">{renderLog(item)}</td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="flex items-center justify-between px-4 py-3 border-t border-slate-100 text-xs text-slate-600 bg-slate-50">
          <span>
            Showing {total === 0 ? 0 : start + 1} to {Math.min(end, total)} of {total} entries
            {searchTerm && ` (filtered from ${parties.length} total)`}
          </span>
          <div className="flex items-center gap-1">
            <button
              className="px-2 py-1 border border-slate-300 rounded disabled:opacity-30 hover:bg-slate-100"
              disabled={currentPage <= 1 || itemsPerPage === "all"}
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
            >
              ←
            </button>
            {Array.from({ length: pages }, (_, i) => i + 1).map((pg) => (
              <button
                key={pg}
                className={`px-2.5 py-1 border rounded text-xs ${
                  currentPage === pg
                    ? "bg-blue-600 text-white border-blue-600"
                    : "border-slate-300 hover:bg-slate-100"
                }`}
                onClick={() => setCurrentPage(pg)}
              >
                {pg}
              </button>
            ))}
            <button
              className="px-2 py-1 border border-slate-300 rounded disabled:opacity-30 hover:bg-slate-100"
              disabled={currentPage >= pages || itemsPerPage === "all"}
              onClick={() => setCurrentPage((p) => Math.min(pages, p + 1))}
            >
              →
            </button>
          </div>
        </div>
      </div>

      {/* Delete dialog */}
      <AlertDialog open={deleteConfirm !== null} onOpenChange={() => setDeleteConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this party?</AlertDialogTitle>
            <AlertDialogDescription>This action cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deleteConfirm && handleDelete(deleteConfirm)}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default BusinessParty;
